import { openSafeLink } from "./link-safety";
import { isArray, isObject, isObjectLike, merge, mergeWith } from "lodash";
import {
  IcDiscordForm,
  IcFacebookForm,
  IcLinkForm,
  IcTelegramForm,
  IcTwitterForm,
} from "~/src/assets/icon";
import { ImgBrown, ImgGold, ImgSilver } from "~/src/assets/img";
import { type Token } from "../apis/account-balance";
import axios from "axios";
import { ChainIconList } from "./chains";
import multiavatar from "@multiavatar/multiavatar";

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
export const WEBSITE_URL = import.meta.env.VITE_WEBSITE_URL;
export const ACCOUNT_BALANCE_URL = import.meta.env.VITE_ACCOUNT_BALANCE;
export const IMAGE_KIT_PRIVATE_KEY = import.meta.env.VITE_IMAGE_KIT_PRIVATE_KEY;
export const MORALIS_BASE_URL = import.meta.env.VITE_MORALIS_BASE_URL;
export const EXCHANGE_RATE_URL = `${import.meta.env.VITE_BASE_URL}/1/balance/v1/exchange-rates/by-symbol`;

export const REQUEST_TIMEOUT_IN_SECOND = 20;

// FILE SIZE
export const ONE_MB = 1048576; // 1 MB
export const MAX_IMAGE_SIZE = ONE_MB * 5;
export const MAX_IMAGE_SIZE_TO_COMPRESS = ONE_MB * 25;
export const MAX_FILE_SIZE = ONE_MB * 25; // 25MB
export const MAX_PARTY_CHAT_VIDEO_SIZE = ONE_MB * 500;
export const FILE_PART_SIZE = ONE_MB * 10;

export const CHAT_MIN_DISPLAY_NAME = 2;
export const CHAT_MAX_DISPLAY_NAME = 30;
export const CHAT_MAX_STATUS = 120;
export const CHAT_MAX_BIO = 600;
export const CHAT_MAX_WIDTH_IMAGE_SIZE = 1080;
export const CHAT_MAX_HEIGHT_IMAGE_SIZE = 1080;
export const TYPING_MESSAGE_DURATION = 3000;
export const PARTY_NAME_LENGTH = 32;
export const PARTY_DESC_LENGTH = 400;
export const MAX_PARTY_CHAT_NAME = 32;
export const MAX_PARTY_CHAT_DES = 400;
export const PAY_TO_OPEN_DES_LENGTH = 1000;

export const CHAT_SUPPORTED_IMAGE_EXTENSIONS = [
  "png",
  "jpg",
  "jpeg",
  "heic",
  "gif",
];
export const CHAT_SUPPORTED_VIDEO_EXTENSIONS = ["mp4", "mov"];
export const CHAT_SUPPORTED_AUDIO_EXTENSIONS = ["mp3", "m4a"];

export enum MENU_LINK {
  LOGIN = "/login",
  VIEW_PROFILE = "/profile",
  EDIT_PROFILE = "/edit-profile",
  SECURE_CHAT = "/secure-chat",
  SETTINGS = "/settings",
  NOT_FOUND = "/not-found",
}

export const DOWNLOAD_AVACUS_LINK = {
  IOS: "https://apps.apple.com/vn/app/avacus-web3/id6449657442",
  ANDROID: "https://play.google.com/store/apps/details?id=io.sowaka.avacus",
};

// Include both mainnet and testnet
export enum SupportNetworks {
  Ethereum = "Ethereum",
  BSC = "BSC",
  Polygon = "Polygon",
}

export interface SocialType {
  twitter?: string;
  facebook?: string;
  discord?: string;
  telegram?: string;
  website?: string;
}

export const SOCIAL_ICON: Record<string, string> = {
  twitter: IcTwitterForm,
  facebook: IcFacebookForm,
  discord: IcDiscordForm,
  telegram: IcTelegramForm,
  website: IcLinkForm,
};

export const openInNewTab = openSafeLink;

export const getShortName = (
  name: string,
  num = 30,
  suffix = "..."
): string => {
  const characters = Array.from(name);
  if (characters.length < num) return name;
  return `${characters.slice(0, num).join("")}${suffix}`;
};

export const MINIUM_TOP = 1;

export const RANKING_CORE = {
  GOLD: 90.0,
  SILVER: 75.0,
  BRONZE: 50.0,
};

export enum RANKING_LEVEL {
  GOLD = "GOLD",
  SILVER = "SILVER",
  BRONZE = "BRONZE",
  NONE = "NONE",
}

export const RANKING_IMAGE = {
  GOLD: ImgGold,
  SILVER: ImgSilver,
  BRONZE: ImgBrown,
  NONE: null,
};

export const getRankingLevelImage: any = (rankingCore: number) => {
  if (rankingCore < RANKING_CORE.BRONZE) {
    return RANKING_IMAGE.NONE;
  } else if (
    rankingCore >= RANKING_CORE.BRONZE &&
    rankingCore < RANKING_CORE.SILVER
  ) {
    return RANKING_IMAGE.BRONZE;
  } else if (
    rankingCore >= RANKING_CORE.SILVER &&
    rankingCore < RANKING_CORE.GOLD
  ) {
    return RANKING_IMAGE.SILVER;
  } else {
    return RANKING_IMAGE.GOLD;
  }
};

export const removeTrailingZeros: any = (num: string, decimals = 2) => {
  return parseFloat(Number(num).toFixed(decimals)).toString();
};

export const formatDollar: any = (value: number) => {
  const USDollar = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(value);

  return USDollar;
};

export const sumBalanceOfAccount: any = (object: object) => {
  if (!object || !Object.keys(object).length) return 0;
  const values = Object.values(object).filter((item) => item >= MINIUM_TOP);

  const sum = values.reduce((accumulator, value) => {
    return accumulator + value;
  }, 0);
  if (sum >= 999.995 && sum < 999999) {
    return formatDollar(sum / 1000) + "K";
  } else if (sum >= 999999 && sum < 999999999) {
    return formatDollar(sum / 1000000) + "M";
  } else if (sum >= 999999999) {
    return formatDollar(sum / 1000000000) + "B";
  } else {
    return formatDollar(sum);
  }
};

const covertNetworkName: any = (name: string) => {
  let network = "";
  switch (name) {
    case "chapel":
    case "bsc":
      network = SupportNetworks.BSC;
      break;
    case "polygon":
    case "mumbai":
      network = SupportNetworks.Polygon;
      break;
    case "rinkeby":
    case "ethereum":
      network = SupportNetworks.Ethereum;
      break;
    default:
      network = name;
  }
  return network;
};

const getObjectKeyByCondition: any = (obj: any) => {
  const keys: any[] = [];
  for (const key of Object.keys(obj)) {
    if (obj[key] >= MINIUM_TOP) {
      const network = covertNetworkName(key);
      keys.push(network);
    }
  }
  return keys?.map((k) => k.toUpperCase());
};

export const getTopNetworks: any = (object: object) => {
  if (!object || !Object.keys(object).length) return [];
  const networks = getObjectKeyByCondition(object);
  const networkList = networks.map((network: any) => {
    return {
      name: network,
      image_url: ChainIconList[network],
    };
  });
  return networkList;
};

export const getTopTokens: any = (tokens: Token[]) => {
  if (!tokens?.length) return [];
  const topTokens = tokens
    .filter((token) => token.price_in_fiat >= MINIUM_TOP)
    .sort((a, b) => {
      return b.price_in_fiat - a.price_in_fiat;
    });
  return topTokens;
};

export const formatIntegerNumber: any = (number: number) => {
  if (!number) return 0;
  else if (number >= 1000 && number < 1000000) {
    return (number / 1000).toFixed(1) + "K";
  } else if (number >= 1000000 && number < 1000000000) {
    return (number / 1000000).toFixed(1) + "M";
  } else if (number >= 1000000000) {
    return (number / 1000000000).toFixed(1) + "B";
  } else {
    return number;
  }
};

export enum MediaType {
  IMAGE = "image",
  VIDEO = "video",
  AUDIO = "audio",
}

export const isImage: any = (url: string) => {
  if (url?.includes(".gif")) return true;
  return /\.(jpg|jpeg|png|webp|avif|gif|svg|bmp|ico|tiff|heic|heif|glb)(\?.*)?$/.test(
    url
  );
};
export const isVideo: any = (url: string) => {
  return /\.(mp4|3gp|amv|avi|mkv|mpg|vob|webm|wmv|3g2)$/.test(url);
};

export const isAudio: any = (url: string) => {
  return /\.(mp3|m4a|acc|ac3|flac|opus|wav|wma|ogg)$/.test(url);
};

export const checkURLType: any = (url: string) => {
  if (!url) return null;
  else if (isImage(url)) {
    return MediaType.IMAGE;
  } else if (isVideo(url)) {
    return MediaType.VIDEO;
  } else if (isAudio(url)) {
    return MediaType.AUDIO;
  } else {
    return MediaType.IMAGE;
  }
};

export const convertImageURL: any = (imageURL: string) => {
  if (!imageURL) {
    return "";
  } else if (imageURL?.startsWith("ipfs://")) {
    return imageURL.replace("ipfs://", "https://ipfs.io/ipfs/");
  } else if (imageURL?.includes("sequence.info")) {
    return imageURL?.replace("sequence.info", "io");
  }
  return imageURL;
};

/**
 * Checks if an image URL is a proxy URL that should skip image optimization
 * to prevent "Input Buffer is empty" errors
 */
export const isProxyImageUrl: any = (
  url: string | undefined | null
): boolean => {
  if (!url) return false;
  // Check for image-proxy pattern or proxy/v1/meta pattern
  return url.includes("image-proxy") || url.includes("/proxy/v1/meta");
};

export const isHttpUri: any = (uri: string) => {
  if (!uri || (uri && isObjectLike(uri))) return false;
  return uri.startsWith("http://") || uri.startsWith("https://");
};

export const convertHttpUri: any = (uri: string) => {
  if (!!uri && !isHttpUri(uri)) {
    return (uri = "https://" + uri);
  }
  return uri;
};

export const getAvatarByWalletAddress = (walletAddress?: string): string => {
  if (!walletAddress) return "";

  // Use multiavatar library to generate avatar locally (offline, no API needed)
  // The library generates SVG string directly from wallet address
  try {
    // multiavatar() returns SVG string directly, not an object
    const svgString = multiavatar(walletAddress.toLowerCase());
    // Convert SVG to data URL (using URL encoding for better compatibility)
    const encoded = encodeURIComponent(svgString);
    return `data:image/svg+xml;charset=utf-8,${encoded}`;
  } catch (error) {
    console.error("Error generating avatar with multiavatar:", error);
    // Fallback to empty string if generation fails
    return "";
  }
};

export const bytesToSize = (bytes: number, seperator = " "): string => {
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  if (bytes === 0) return "N/A";
  const i = parseInt(
    Math.floor(Math.log(bytes) / Math.log(1024)).toString(),
    10
  );
  if (i === 0) return `${bytes}${seperator}${sizes[i]}`;
  return `${(bytes / 1024 ** i).toFixed(2)}${seperator}${sizes[i]}`;
};

export const bytesToMB = (bytes: number, seperator = " "): string => {
  if (!bytes) return "0MB";
  return `${(bytes / ONE_MB).toFixed(2)}${seperator}MB`;
};

export const fileNameShorten: any = (fileName: string, num: number) => {
  if (!fileName) return "";
  if (!num) num = 20;
  if (fileName.length - 6 > num) {
    const prefix = fileName.slice(0, num);
    const suffix = fileName?.split(".")?.pop();
    return `${prefix}...${suffix}`;
  }
  return fileName;
};

export const hasMultipleLines = (
  divElement: HTMLDivElement | null
): boolean => {
  if (!divElement) return false;
  const lineHeight = parseInt(window.getComputedStyle(divElement).lineHeight);
  const contentHeight = divElement.clientHeight;
  const numberOfLines = Math.round(contentHeight / lineHeight);
  return numberOfLines > 1;
};

export const capitalizeFirstLetter = (str: string): string => {
  if (!str || str.length === 0) return "";
  return str?.charAt(0).toUpperCase() + str?.slice(1);
};

export const formatDecimalRemoveZeros = (num: number | string): string => {
  const numStr = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 10,
  }).format(Number(num));
  return numStr;
};

export const getProfileDetailURL = (walletAddress?: string): string => {
  if (walletAddress) {
    const profileUrl = `${MENU_LINK.VIEW_PROFILE}/${walletAddress}`;
    return profileUrl;
  }
  return "";
};

export const mergedObject = (originalObject: any, updates: any): any => {
  return mergeWith(originalObject, updates, (objValue, srcValue) => {
    // Custom merge logic goes here
    if (isArray(objValue) && isArray(srcValue)) {
      // Update all items in the array
      return srcValue.map((item, index) => merge({}, objValue[index], item));
    } else if (isObject(objValue)) {
      // Recursively merge objects
      return merge({}, objValue, srcValue);
    } else {
      // Replace scalar values
      return srcValue;
    }
  });
};

export const shareOnSocialNetwork = (platform: string, url: string): any => {
  if (typeof window === "undefined") return;
  let popUrl = "";
  let newWindow: any = null;

  if (platform === "twitter") {
    popUrl = "https://twitter.com/intent/tweet?text=" + url;
  } else if (platform === "facebook") {
    popUrl = "https://www.facebook.com/sharer/sharer.php?u=" + url;
  }
  newWindow = window.open(popUrl, "name", "height=500,width=600");
  newWindow?.focus();
};

export const exchangeCoinToUsd = async (
  tokenSymbols: string[]
): Promise<any> => {
  try {
    const res = await axios.post(EXCHANGE_RATE_URL, {
      symbols: tokenSymbols,
      fiats: ["usd"],
    });
    const data: Record<string, { usd: string }> = res?.data?.data;
    return data;
  } catch (error: any) {
    console.error("Error fetching exchange rate:", error.message);
    return 0;
  }
};
